import { types, flow, getSnapshot, getParent } from 'mobx-state-tree';
import { ApiStore } from './ApiStore';
import { SalesPerformance, NullSalesPerformance } from './SalesPerformance';

const toMaybeString = v => (v == null || typeof v === 'string') ? v : undefined;
const toString = v => (typeof v === 'string' ? v : '');
const USER_TYPES = ['Company Admin', 'Executive', 'Senior Manager', 'Sales Manager', 'Sales Member', 'admin'];
const USER_TYPE_SET = new Set(USER_TYPES);

const normalizeUserType = raw => {
    if (raw == null) return 'Sales Member';
    return USER_TYPE_SET.has(raw) ? raw : 'Sales Member';
};

const _user = types.model({
    userId: types.string,
    firstName: types.string,
    lastName: types.string,
    email: types.string,
    active: types.optional(types.boolean, true),
    userType: types.optional(types.enumeration(USER_TYPES), 'Sales Member'),
    salesPerformance: types.optional(SalesPerformance, getSnapshot(NullSalesPerformance)),
    stripeCustomerId: types.maybe(types.string),
    subscriptionId: types.maybe(types.string),
    reportsToId: types.maybe(types.string),
    disabled: types.maybe(types.boolean),
    companyIds: types.optional(types.array(types.string), []),
    isReadOnly: types.optional(types.boolean, false),
    emailNotificationsEnabled: types.optional(types.boolean, true),
    // Report Sharing preferences (controlled from /profile → Report Sharing card)
    sendMeetingReports: types.optional(types.boolean, true),
    shareWithExternalParticipants: types.optional(types.boolean, false),
    notifyOnExternalShare: types.optional(types.boolean, true),
    hasCompletedTutorial: types.optional(types.boolean, false),
    // Optional gender override for Murphy grading. When unset, Murphy continues
    // to infer pronouns from firstName (today's behavior). When set, the grader
    // is given the pronouns explicitly. Values: 'male', 'female', 'they/them'.
    gender: types.maybe(types.enumeration(['male', 'female', 'they/them'])),
    // Opt-in to appear on the company leaderboard. Sales Members/Managers and Senior
    // Managers are always shown; Executives and Company Admins are hidden unless this
    // is true (set from the leaderboard widget's own toggle, a /users/{self} edit).
    leaderboardOptIn: types.optional(types.boolean, false)
}).preProcessSnapshot(snapshot => {
    if (!snapshot) return snapshot;
    return {
        ...snapshot,
        // Required string fields — coerce null/undefined/non-string to '' so a
        // single malformed user row in the API response doesn't kill the
        // entire AdminData[] conversion.
        userId: toString(snapshot.userId),
        firstName: toString(snapshot.firstName),
        lastName: toString(snapshot.lastName),
        email: toString(snapshot.email),
        stripeCustomerId: toMaybeString(snapshot.stripeCustomerId),
        subscriptionId: toMaybeString(snapshot.subscriptionId),
        reportsToId: toMaybeString(snapshot.reportsToId),
        userType: normalizeUserType(snapshot.userType),
    };
}).views(self => ({
    get isReadOnlyExecutive() {
        return self.userType === 'Executive' && self.isReadOnly;
    },
    get isReadOnlySalesManager() {
        return self.userType === 'Sales Manager' && self.isReadOnly;
    },
    get isReadOnlySeniorManager() {
        return self.userType === 'Senior Manager' && self.isReadOnly;
    },
    get isExecutive() {
        return self.userType === 'Company Admin' || self.userType === 'Executive' || self.userType === 'admin';
    },
    get isSeniorManager() {
        return self.userType === 'Senior Manager';
    },
    get isCompanyAdmin() {
        return self.userType === 'Company Admin';
    },
    get isAdmin() {
        return self.userType === 'admin';
    },
    get canChangeCreditCard() {
        return !!self.stripeCustomerId;
    },
    get isSalesMember() {
        return self.userType === 'Sales Member';
    },
    get fullName() {
        return `${self.firstName} ${self.lastName}`;
    },
    get hasMoreThanOneCompany() {
        return self.companyIds.length > 1;
    }
}));

export const User = types.compose('User', _user, ApiStore).actions(self => ({
    updateFromSnapshot: flow(function* updateFromSnapshot(snapshot) {
        const patch = {
            firstName: snapshot.firstName,
            lastName: snapshot.lastName,
            userType: snapshot.userType,
            reportsToId: snapshot.reportsToId,
            companyIds: snapshot.companyIds,
            isReadOnly: snapshot.isReadOnly,
            emailNotificationsEnabled: snapshot.emailNotificationsEnabled,
            sendMeetingReports: snapshot.sendMeetingReports,
            shareWithExternalParticipants: snapshot.shareWithExternalParticipants,
            notifyOnExternalShare: snapshot.notifyOnExternalShare,
            // Send explicit null when cleared so patchHandler REMOVEs the attribute;
            // undefined gets dropped by JSON.stringify and the field would persist forever.
            gender: snapshot.gender ?? null,
            leaderboardOptIn: snapshot.leaderboardOptIn ?? false
        };

        // Only include active if it actually changed to avoid triggering spurious activation emails
        if (snapshot.active !== self.active) {
            patch.active = snapshot.active;
        }

        yield self.patchApi(`/users/${self.userId ?? snapshot.userId}`, patch, self);
    }),
    // Targeted opt-in write — PATCHes ONLY leaderboardOptIn so toggling
    // leaderboard visibility never re-writes role / companyIds / reportsToId /
    // gender (avoids the full-profile re-send that updateFromSnapshot does and
    // the lost-update race that comes with it).
    setLeaderboardOptIn: flow(function* setLeaderboardOptIn(value) {
        yield self.patchApi(`/users/${self.userId}`, { leaderboardOptIn: value }, self);
    }),
    createFromSnapshot: flow(function* createFromSnapshot(snapshot) {
        snapshot.active = true;
        yield self.postApi('/users', snapshot, self);
    }),
    addUserToCompany: flow(function* addUserToCompany(companyId) {
        self.companyIds.push(companyId);

        yield self.updateFromSnapshot(self);
    }),
    removeUserFromCompany: flow(function* removeUserFromCompany(companyId) {
        self.companyIds = self.companyIds.filter(id => id !== companyId);

        yield self.updateFromSnapshot(self);
    }),
    delete: flow(function* deleteUser() {
        yield self.deleteApi(`/users/${self.userId}`);
    }),
    setHasCompletedTutorial(value) {
        self.hasCompletedTutorial = value;
    },
}));

export const NullUser = User.create(
    {
        userId: '',
        firstName: '',
        lastName: '',
        companyIds: [],
        email: '',
        active: true,
        userType: 'Company Admin'
    }
);
